import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  Plus,
  Users,
  X,
  ChevronDown,
  Minus,
  ArrowRight,
  Check,
  UploadCloud,
  Download,
  FileSpreadsheet,
} from "lucide-react";
import ExpandableSearchBar from "../components/ExpandableSearchBar";
import {
  leadsApi,
  teamsApi,
  employeesApi,
  dashboardApi,
  orderCampaignApi,
  floorsApi,
} from "../services/api";
import BulkLeadUploadModal from "../components/BulkLeadUploadModal";
import CampaignSelectionModal from "../components/CampaignSelectionModal";
import SuccessModal from "../components/SuccessModal";
import { leadStatusOptions } from "../utils/constants";
import AddLeadModal from "../components/AddLeadModal";
import LeadHistoryModal from "../components/LeadHistoryModal";
import { useTheme } from "../context/ThemeContext";
import ThemeSelect from "../components/ThemeSelect";
import { Calendar, Tag, CreditCard, Layout, CalendarDays, Eye, History } from "lucide-react";
import { message, DatePicker } from "antd";
import dayjs from "dayjs";

import { usePermissions } from "../hooks/Usepermissions";
import Pagination from "../components/Pagination";
import ExcelExportButton from "../components/ExcelExportButton";
import DateRangeFilter from "../components/DateRangeFilter";

const { RangePicker } = DatePicker;

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "N/A";
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.getMonth() + 1;
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const formattedHour = String(hours).padStart(2, "0");
  return `${day}/${month}/${year}, ${formattedHour}:${minutes} ${ampm}`;
};

function Leads() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === "#FFFFFF";
  const navigate = useNavigate();
  const { has, isAdmin } = usePermissions();

  // ── Main State ─────────────────────────────────────────────────────────────
  const [activeMainTab, setActiveMainTab] = useState("Dashboard");
  const [activeTab, setActiveTab] = useState("Fresh");
  const [activeStatusTab, setActiveStatusTab] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [freshCount, setFreshCount] = useState(0);
  const [assignedCount, setAssignedCount] = useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedHistoryLead, setSelectedHistoryLead] = useState(null);

  // ✅ floorId added in both filter states
  const [activeFilters, setActiveFilters] = useState({
    team: "",
    source: "",
    dateType: "created",
    assignedDate: "today",
    agent: "",
    category: "",
    status: "",
    product: "",
    campaign: "",
    limit: "10",
    customStartDate: "",
    customEndDate: "",
    dispositionId: "",
    entryDate: "",
    floorId: "",
  });

  const [tempFilters, setTempFilters] = useState({
    team: "",
    source: "",
    dateType: "created",
    assignedDate: "today",
    agent: "",
    category: "",
    status: "",
    product: "",
    campaign: "",
    limit: "10",
    customStartDate: "",
    customEndDate: "",
    dispositionId: "",
    entryDate: "",
    floorId: "",
  });

  const [isAgentDropdownOpen, setIsAgentDropdownOpen] = useState(false);
  const [agentSearchTerm, setAgentSearchTerm] = useState("");
  const [allEmployees, setAllEmployees] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [floors, setFloors] = useState([]); // ✅ NEW
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [teamStats, setTeamStats] = useState({
    totalLeads: 0,
    callBack: 0,
    falseWrongNumber: 0,
    interestedOnHold: 0,
    notInterested: 0,
    notConnected: 0,
    switchOff: 0,
    orderPlaced: 0,
    targetRevenue: 0,
    actualRevenue: 0,
    dispositions: [],
    untaggedLeads: 0,
  });
  const [teamStatsLoading, setTeamStatsLoading] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [teams, setTeams] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [topAgents, setTopAgents] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const limitDebounceRef = useRef(null);
  const [stats, setStats] = useState({
    totalLeads: 0,
    untaggedLeads: 0,
    dispositions: [],
    totalDispositions: 0,
    callBack: 0,
    falseWrongNumber: 0,
    interestedOnHold: 0,
    notInterested: 0,
    notConnected: 0,
    switchOff: 0,
    orderPlaced: 0,
    targetRevenue: 500000,
    actualRevenue: 0,
  });
  const [newLead, setNewLead] = useState({
    source: "JUST_DIAL",
    customerName: "",
    contactNumber: "",
  });
  const [leadType, setLeadType] = useState("single");
  const fileInputRef = useRef(null);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");

  // ── Date Popover State ─────────────────────────────────────────────────────
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(
    activeFilters.customStartDate || "",
  );
  const [tempEndDate, setTempEndDate] = useState(
    activeFilters.customEndDate || "",
  );
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const datePopoverRef = useRef(null);

  // ── Entry Leads State ──────────────────────────────────────────────────────
  const [entryLeadsData, setEntryLeadsData] = useState({
    leads: [],
    totalCount: 0,
    assignedCount: 0,
    unassignedCount: 0,
    totalEntryCount: 0,
    sourceWiseCounts: [],
    dateWiseCounts: [],
  });
  const [entryLeadsLoading, setEntryLeadsLoading] = useState(false);
  const [activeSourceFilter, setActiveSourceFilter] = useState("");
  const [activeAssignFilter, setActiveAssignFilter] = useState("");
  const [entryCurrentPage, setEntryCurrentPage] = useState(1);

  // ── Sync temp dates ────────────────────────────────────────────────────────
  useEffect(() => {
    setTempStartDate(activeFilters.customStartDate || "");
    setTempEndDate(activeFilters.customEndDate || "");
  }, [activeFilters.customStartDate, activeFilters.customEndDate]);

  // ── Click outside date popover ────────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePopoverRef.current &&
        !datePopoverRef.current.contains(event.target)
      ) {
        setIsDatePopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getDateRangeLabel = () => {
    const formatDateStr = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";
      return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    };
    if (activeFilters.assignedDate === "custom") {
      if (activeFilters.customStartDate && activeFilters.customEndDate)
        return `${formatDateStr(activeFilters.customStartDate)} - ${formatDateStr(activeFilters.customEndDate)}`;
      if (activeFilters.customStartDate)
        return `From ${formatDateStr(activeFilters.customStartDate)}`;
      if (activeFilters.customEndDate)
        return `To ${formatDateStr(activeFilters.customEndDate)}`;
      return "Custom Range";
    }
    const presets = {
      "": "All View",
      today: "Today",
      yesterday: "Yesterday",
      this_week: "This Week",
      last_week: "Last Week",
      last_10_days: "Last 10 Days",
      last_30_days: "Last 30 Days",
      this_month: "This Month",
      last_month: "Last Month",
      this_year: "This Year",
      last_year: "Last Year",
    };
    return presets[activeFilters.assignedDate] || "All View";
  };

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchAllEmployees();
    fetchCampaigns();
    fetchFloors(); // ✅ NEW
    initPage();
  }, []);

  useEffect(() => {
    if (teams.length === 0) return;
    const query = buildQuery(activeFilters, activeTab, teams);
    fetchLeads(query, activeTab);
    fetchDashboardStats(query);
  }, [
    activeFilters.source,
    activeFilters.assignedDate,
    activeFilters.status,
    activeFilters.dispositionId,
    activeFilters.agent,
    activeFilters.campaign,
    activeFilters.customStartDate,
    activeFilters.customEndDate,
    activeFilters.floorId, // ✅ NEW
    activeTab,
  ]);

  // ── Entry Date change → switch to Entry Leads tab ─────────────────────────
  useEffect(() => {
    if (activeFilters.entryDate) {
      setActiveTab("Entry Leads");
      setActiveSourceFilter("");
      setActiveAssignFilter("");
      setEntryCurrentPage(1);
      fetchEntryLeads(activeFilters.entryDate, "", "", 0);
    }
  }, [activeFilters.entryDate]);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, activeStatusTab, leads, activeTab, activeFilters]);

  // ── Init ───────────────────────────────────────────────────────────────────
  const initPage = async () => {
    try {
      const teamsData = await teamsApi.getAllTeams();
      setTeams(teamsData || []);
      const firstTeam = teamsData?.[0];
      if (firstTeam) {
        setSelectedTeamId(firstTeam.id);
        setSelectedTeam(firstTeam.name);
      }

      const query = buildQuery(activeFilters, activeTab, teamsData || []);
      const freshQ = buildQuery(activeFilters, "Fresh", teamsData || []);
      const assignedQ = buildQuery(activeFilters, "Assigned", teamsData || []);

      // activeTab ka query aur uske tab ka count-query bilkul same hote hain, to
      // us tab ke liye dobara fetchLeads mat karo — warna wahi count API 2 baar
      // hit hoti thi (default activeTab = "Fresh").
      await Promise.all([
        fetchLeads(query, activeTab, true),
        activeTab === "Fresh"
          ? Promise.resolve()
          : fetchLeads(freshQ, "Fresh", false),
        activeTab === "Assigned"
          ? Promise.resolve()
          : fetchLeads(assignedQ, "Assigned", false),
        fetchDashboardStats(query),
        firstTeam ? fetchTeamStats(firstTeam.id) : Promise.resolve(),
      ]);
    } catch (error) {
      console.error("Init error:", error);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const resolveDateRange = (preset, customStart = "", customEnd = "") => {
    const now = new Date();
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const fmtStart = (d) => {
      d.setHours(0, 0, 0, 0);
      return fmt(d);
    };
    const fmtEnd = (d) => {
      d.setHours(23, 59, 59, 999);
      return fmt(d);
    };
    switch (preset) {
      case "today":
        return {
          startDate: fmtStart(new Date(now)),
          endDate: fmtEnd(new Date(now)),
        };
      case "yesterday": {
        const y = new Date(now);
        y.setDate(y.getDate() - 1);
        return { startDate: fmtStart(y), endDate: fmtEnd(y) };
      }
      case "this_week": {
        const d = now.getDay(),
          diff = now.getDate() - d + (d === 0 ? -6 : 1),
          s = new Date(now);
        s.setDate(diff);
        return { startDate: fmtStart(s), endDate: fmtEnd(now) };
      }
      case "last_week": {
        const d = now.getDay(),
          diff = now.getDate() - d + (d === 0 ? -6 : 1),
          ts = new Date(now);
        ts.setDate(diff);
        const ls = new Date(ts);
        ls.setDate(ls.getDate() - 7);
        const le = new Date(ts);
        le.setDate(le.getDate() - 1);
        return { startDate: fmtStart(ls), endDate: fmtEnd(le) };
      }
      case "last_10_days": {
        const s = new Date(now);
        s.setDate(s.getDate() - 10);
        return { startDate: fmtStart(s), endDate: fmtEnd(now) };
      }
      case "last_30_days": {
        const s = new Date(now);
        s.setDate(s.getDate() - 30);
        return { startDate: fmtStart(s), endDate: fmtEnd(now) };
      }
      case "this_month":
        return {
          startDate: fmtStart(new Date(now.getFullYear(), now.getMonth(), 1)),
          endDate: fmtEnd(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
        };
      case "last_month":
        return {
          startDate: fmtStart(
            new Date(now.getFullYear(), now.getMonth() - 1, 1),
          ),
          endDate: fmtEnd(new Date(now.getFullYear(), now.getMonth(), 0)),
        };
      case "this_year":
        return {
          startDate: fmtStart(new Date(now.getFullYear(), 0, 1)),
          endDate: fmtEnd(new Date(now.getFullYear(), 11, 31)),
        };
      case "last_year":
        return {
          startDate: fmtStart(new Date(now.getFullYear() - 1, 0, 1)),
          endDate: fmtEnd(new Date(now.getFullYear() - 1, 11, 31)),
        };
      case "custom":
        return { startDate: customStart || "", endDate: customEnd || "" };
      default:
        return { startDate: "", endDate: "" };
    }
  };

  // ✅ buildQuery — floorId added
  const buildQuery = (
    filters,
    currentTab = activeTab,
    teamsData = teams,
    page = 0,
  ) => {
    const params = new URLSearchParams();
    const { startDate, endDate } = resolveDateRange(
      filters.assignedDate,
      filters.customStartDate,
      filters.customEndDate,
    );
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (filters.status && !filters.dispositionId)
      params.append("status", filters.status);
    if (filters.dispositionId)
      params.append("dispositionId", filters.dispositionId);
    if (filters.entryDate && currentTab === "Entry Leads")
      params.append("entryDate", filters.entryDate);

    // ✅ Floor filter
    if (filters.floorId) params.append("floorId", filters.floorId);

    if (filters.team) {
      const teamObj = teamsData.find(
        (t) => String(t.id) === String(filters.team),
      );
      const teamName = teamObj?.name || teamObj?.teamName;
      if (teamName) params.append("teamName", teamName);
    }
    if (filters.source) params.append("source", filters.source);
    if (filters.agent && filters.agent.length > 0)
      filters.agent.forEach((a) => params.append("employeeId", a));
    if (filters.campaign) params.append("campaignName", filters.campaign);
    if (currentTab === "Fresh") params.append("assigned", "false");
    else if (currentTab === "Assigned") params.append("assigned", "true");
    params.append("page", String(page));
    params.append(
      "size",
      filters.limit === "all" ? "500" : filters.limit || "10",
    );
    return `?${params.toString()}`;
  };

  const extractLeadsArray = (data) =>
    Array.isArray(data)
      ? data
      : Array.isArray(data?.leads)
        ? data.leads
        : Array.isArray(data?.content)
          ? data.content
          : Array.isArray(data?.data)
            ? data.data
            : [];

  // ── Fetch Functions ────────────────────────────────────────────────────────

  // ✅ NEW — fetch floors
  const fetchFloors = async () => {
    try {
      const data = await floorsApi.getAllFloors();
      setFloors(data || []);
    } catch {
      setFloors([]);
    }
  };

  const fetchLeads = async (
    query = "",
    currentTab = "Fresh",
    updateTable = true,
  ) => {
    try {
      if (updateTable) setLoading(true);
      const p = new URLSearchParams(query.replace("?", ""));

      if (currentTab === "Fresh") {
        const countParams = new URLSearchParams(query.replace("?", ""));
        countParams.set("assigned", "false");
        countParams.set("page", "0");
        countParams.set("size", "1");
        const countData = await leadsApi.getAllLeads(
          `?${countParams.toString()}`,
        );
        setFreshCount(countData?.totalCount || countData?.totalElements || 0);
        if (updateTable) {
          p.set("assigned", "false");
          const data = await leadsApi.getAllLeads(`?${p.toString()}`);
          const arr = extractLeadsArray(data);
          setLeads(arr);
          setFilteredLeads(arr);
          const total = data?.totalCount || data?.totalElements || arr.length;
          setTotalMatches(total);
          setTotalLeads(total);
        }
        return;
      }

      if (currentTab === "Assigned") {
        p.set("assigned", "true");
        if (updateTable) {
          const data = await leadsApi.getAllLeads(`?${p.toString()}`);
          const arr = extractLeadsArray(data);
          setLeads(arr);
          setFilteredLeads(arr);
          const total = data?.totalCount || data?.totalElements || arr.length;
          setTotalMatches(total);
          setTotalLeads(total);
          setAssignedCount(total);
        }
      }
    } catch (error) {
      console.error("Error fetching leads:", error);
      if (updateTable) {
        setLeads([]);
        setFilteredLeads([]);
        setTotalMatches(0);
        setTotalLeads(0);
      }
    } finally {
      if (updateTable) setLoading(false);
    }
  };

  // ✅ fetchEntryLeads — floorId added
  const fetchEntryLeads = async (
    entryDate,
    sourceFilter = "",
    assignFilter = "",
    page = 0,
  ) => {
    if (!entryDate) return;
    try {
      setEntryLeadsLoading(true);
      const params = new URLSearchParams();
      params.append("entryDate", entryDate);

      if (sourceFilter && sourceFilter.trim())
        params.append("source", sourceFilter.trim());

      if (assignFilter === "assigned") params.append("assigned", "true");
      if (assignFilter === "unassigned") params.append("assigned", "false");

      // ✅ Floor filter
      if (activeFilters.floorId)
        params.append("floorId", activeFilters.floorId);

      params.append("page", String(page));
      params.append(
        "size",
        activeFilters.limit === "all" ? "500" : activeFilters.limit || "20",
      );

      const data = await leadsApi.getAllLeads(`?${params.toString()}`);
      setEntryLeadsData({
        leads: data?.leads || extractLeadsArray(data),
        totalCount: data?.totalCount || 0,
        assignedCount: data?.assignedCount || 0,
        unassignedCount: data?.unassignedCount || 0,
        totalEntryCount: data?.totalEntryCount || data?.totalCount || 0,
        sourceWiseCounts: data?.sourceWiseCounts || [],
        dateWiseCounts: data?.dateWiseCounts || [],
      });
    } catch (error) {
      console.error("Entry leads fetch error:", error);
      setEntryLeadsData({
        leads: [],
        totalCount: 0,
        assignedCount: 0,
        unassignedCount: 0,
        totalEntryCount: 0,
        sourceWiseCounts: [],
        dateWiseCounts: [],
      });
    } finally {
      setEntryLeadsLoading(false);
    }
  };

  const fetchDashboardStats = async (query = "") => {
    try {
      const summaryData = await dashboardApi.getLeadsSummary(query);
      const statusBreakdown = await dashboardApi.getLeadStatusBreakdown(query);
      if (summaryData || statusBreakdown) {
        setStats({
          totalLeads: summaryData?.totalLeads || 0,
          untaggedLeads: summaryData?.untaggedLeads || 0,
          dispositions: summaryData?.dispositions || [],
          totalDispositions: summaryData?.totalDispositions || 0,
          callBack:
            statusBreakdown?.callBack || statusBreakdown?.CALL_BACK || 0,
          falseWrongNumber:
            statusBreakdown?.prankCall || statusBreakdown?.PRANK_CALL || 0,
          interestedOnHold:
            statusBreakdown?.interestedOnHold ||
            statusBreakdown?.INTERESTED_ON_HOLD ||
            0,
          notInterested:
            statusBreakdown?.notInterested ||
            statusBreakdown?.NOT_INTERESTED ||
            0,
          notConnected:
            statusBreakdown?.notConnected ||
            statusBreakdown?.NOT_CONNECTED ||
            0,
          switchOff:
            statusBreakdown?.switchOff || statusBreakdown?.SWITCH_OFF || 0,
          orderPlaced: statusBreakdown?.orderPlaced || 0,
          targetRevenue: summaryData?.targetRevenue || 500000,
          actualRevenue:
            summaryData?.actualRevenue || summaryData?.totalRevenue || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchAllEmployees = async () => {
    const dummy = [];
    try {
      const data = await employeesApi.getAllEmployees();
      setAllEmployees([...(data || []), ...dummy]);
    } catch {
      setAllEmployees(dummy);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const data = await orderCampaignApi.getAllCampaigns();
      setCampaigns(data || []);
    } catch {
      setCampaigns([]);
    }
  };

  const fetchTopAgents = async () => {
    try {
      const response = await leadsApi.getTopPerformersAPI();
      const performers = response?.topPerformers || [];
      const top = performers.map((agent) => ({
        id: agent.userId,
        name: agent.fullName || agent.agentName || "Unknown Agent",
        employeeId: agent.employeeId,
        orders: agent.orderCount || 0,
        avatar:
          agent.profileImage ||
          `https://ui-avatars.com/api/?name=${encodeURIComponent(agent.fullName || "Unknown")}`,
      }));
      setTopAgents(
        top.length > 0
          ? top
          : [
              {
                id: 1,
                name: "No data available",
                employeeId: "N/A",
                orders: 0,
                avatar: "https://ui-avatars.com/api/?name=No+Data",
              },
            ],
      );
    } catch {
      setTopAgents([]);
    }
  };

  const resetTeamStats = () =>
    setTeamStats({
      totalLeads: 0,
      callBack: 0,
      falseWrongNumber: 0,
      interestedOnHold: 0,
      notInterested: 0,
      notConnected: 0,
      switchOff: 0,
      orderPlaced: 0,
      targetRevenue: 0,
      actualRevenue: 0,
      dispositions: [],
      untaggedLeads: 0,
    });

  const fetchTeamStats = async (teamId) => {
    if (!teamId) {
      resetTeamStats();
      return;
    }
    try {
      setTeamStatsLoading(true);
      const baseParams = new URLSearchParams(
        buildQuery(activeFilters).replace("?", ""),
      );
      baseParams.set("teamId", teamId);
      const query = `?${baseParams.toString()}`;
      const [statusCounts, summaryData] = await Promise.all([
        dashboardApi.getLeadStatusBreakdown(query),
        dashboardApi.getLeadsSummary(query),
      ]);
      setTeamStats({
        totalLeads: statusCounts?.totalLeads || 0,
        callBack: statusCounts?.callBack || 0,
        falseWrongNumber: statusCounts?.prankCall || 0,
        interestedOnHold: statusCounts?.interestedOnHold || 0,
        notInterested: statusCounts?.notInterested || 0,
        notConnected: statusCounts?.notConnected || 0,
        switchOff: statusCounts?.switchOff || 0,
        orderPlaced: statusCounts?.orderPlaced || 0,
        dispositions: summaryData?.dispositions || [],
        untaggedLeads: summaryData?.untaggedLeads || 0,
      });
    } catch {
      resetTeamStats();
    } finally {
      setTeamStatsLoading(false);
    }
  };

  const fetchEmployeesByTeam = async (teamId) => {
    try {
      const data = await employeesApi.getEmployeesByTeam(teamId, {
        onlineOnly: true,
        designation: "TELESALES EXECUTIVE",
      });
      return data || [];
    } catch {
      return [];
    }
  };

  // ── Source Options ─────────────────────────────────────────────────────────
  const dynamicSourceOptions = useMemo(() => {
    const sources = new Set();
    leads.forEach((lead) => {
      if (lead.leadSourceRaw) sources.add(lead.leadSourceRaw);
      if (lead.source) sources.add(lead.source);
    });
    const options = [{ label: "All Sources", value: "" }];
    Array.from(sources)
      .sort()
      .forEach((s) => {
        if (s)
          options.push({ label: s.toString().replace(/_/g, " "), value: s });
      });
    return options;
  }, [leads]);

  // ── Filters ────────────────────────────────────────────────────────────────
  const applyFilters = () => {
    let filtered = [...leads];
    if (activeTab === "Assigned") {
      filtered = filtered.filter((lead) => lead.assignedToId !== null);
      if (activeStatusTab && activeStatusTab !== "") {
        if (activeStatusTab.startsWith("DISP_")) {
          const dispId = parseInt(activeStatusTab.replace("DISP_", ""));
          filtered = filtered.filter(
            (lead) =>
              lead.dispositionId === dispId || lead.disposition?.id === dispId,
          );
        } else {
          filtered = filtered.filter((lead) => lead.status === activeStatusTab);
        }
      }
    }
    if (searchTerm) {
      filtered = filtered.filter(
        (lead) =>
          lead.leadId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          lead.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          lead.contactNumber?.includes(searchTerm),
      );
    }
    setTotalMatches(filtered.length);
    setFilteredLeads(filtered);
  };

  const handleStatusTabClick = (tabValue, dispositionId = null) => {
    setActiveStatusTab(tabValue);
    setCurrentPage(1);
    setSelectedLeadIds([]);
    const updatedFilters = {
      ...activeFilters,
      status: dispositionId ? "" : tabValue || "",
      dispositionId: dispositionId ? String(dispositionId) : "",
    };
    const query = buildQuery(updatedFilters, activeTab, teams);
    fetchLeads(query, activeTab);
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleUploadSuccess = async (result) => {
    await fetchLeads();
    await fetchDashboardStats();
    setActiveTab("Fresh");
    setActiveStatusTab("");
    setSelectedLeadIds([]);
    setSelectedEmployees([]);
    setSelectedTeams([]);
    if (result.successCount > 0)
      message.success(`Successfully uploaded ${result.successCount} lead(s)!`);
  };

  // ✅ handleExportExcel — floorId added in both branches
  const handleExportExcel = async () => {
    try {
      setIsDownloading(true);
      const params = new URLSearchParams();

      if (activeTab === "Entry Leads") {
        if (activeFilters.entryDate)
          params.append("entryDate", activeFilters.entryDate);
        if (activeSourceFilter) params.append("source", activeSourceFilter);
        if (activeAssignFilter === "assigned")
          params.append("assigned", "true");
        else if (activeAssignFilter === "unassigned")
          params.append("assigned", "false");
        // ✅ Floor filter for Entry Leads
        if (activeFilters.floorId)
          params.append("floorId", activeFilters.floorId);
      } else {
        const { startDate, endDate } = resolveDateRange(
          activeFilters.assignedDate,
          activeFilters.customStartDate,
          activeFilters.customEndDate,
        );
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        if (activeFilters.status && !activeFilters.dispositionId)
          params.append("status", activeFilters.status);
        if (activeFilters.dispositionId)
          params.append("dispositionId", activeFilters.dispositionId);
        if (activeFilters.source) params.append("source", activeFilters.source);
        if (activeFilters.campaign)
          params.append("campaignName", activeFilters.campaign);
        if (
          Array.isArray(activeFilters.agent) &&
          activeFilters.agent.length > 0
        )
          activeFilters.agent.forEach((a) => params.append("employeeId", a));
        if (activeFilters.team) {
          const teamObj = teams.find(
            (t) => String(t.id) === String(activeFilters.team),
          );
          const teamName = teamObj?.name || teamObj?.teamName;
          if (teamName) params.append("teamName", teamName);
        }
        if (searchTerm) params.append("searchTerm", searchTerm);
        if (activeTab === "Fresh") params.append("assigned", "false");
        else if (activeTab === "Assigned") params.append("assigned", "true");
        // ✅ Floor filter for Fresh/Assigned
        if (activeFilters.floorId)
          params.append("floorId", activeFilters.floorId);
      }

      const query = `?${params.toString()}`;
      const blob = await leadsApi.exportLeadsExcel(query);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Leads_${activeTab}_${activeFilters.entryDate || new Date().toISOString().slice(0, 10)}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success("Download successful!");
    } catch (error) {
      console.error("Export failed:", error);
      message.error("Export failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleAssignClick = () => {
    if (selectedEmployees.length === 0) {
      message.warning("Please select at least one employee");
      return;
    }
    if (selectedLeadIds.length === 0) {
      message.warning("Please select at least one lead");
      return;
    }
    setShowCampaignModal(true);
  };

  const handleAssignLeads = async (selectedCampaign) => {
    try {
      if (
        !window.confirm(
          `Assign ${selectedLeadIds.length} lead(s) to ${selectedEmployees.length} employee(s)?`,
        )
      )
        return;
      setLoading(true);
      let employeeIndex = 0;
      for (const leadId of selectedLeadIds) {
        await leadsApi.assignLeadToUser(
          leadId,
          selectedEmployees[employeeIndex],
          selectedCampaign,
        );
        employeeIndex = (employeeIndex + 1) % selectedEmployees.length;
      }
      setSuccessMessage(
        `Successfully assigned ${selectedLeadIds.length} leads!`,
      );
      setShowSuccessModal(true);
      setShowCampaignModal(false);
      await fetchLeads();
      await fetchDashboardStats();
      setSelectedTeams([]);
      setSelectedEmployees([]);
      setSelectedLeadIds([]);
    } catch (error) {
      console.error("Error assigning leads:", error);
      message.error("Failed to assign leads. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleTeam = async (team) => {
    try {
      const idx = selectedTeams.findIndex((t) => t.id === team.id);
      if (idx > -1) {
        const empIds = selectedTeams[idx].employees?.map((e) => e.id) || [];
        setSelectedTeams(selectedTeams.filter((t) => t.id !== team.id));
        setSelectedEmployees(
          selectedEmployees.filter((id) => !empIds.includes(id)),
        );
        setEmployeeSearchTerm("");
      } else {
        setLoading(true);
        const teamEmployees = await fetchEmployeesByTeam(team.id);
        setSelectedTeams([
          ...selectedTeams,
          { ...team, employees: teamEmployees, expanded: true },
        ]);
        setLoading(false);
      }
    } catch {
      setLoading(false);
    }
  };

  const toggleTeamExpansion = (teamId) => {
    setEmployeeSearchTerm("");
    setSelectedTeams(
      selectedTeams.map((team) =>
        team.id === teamId ? { ...team, expanded: !team.expanded } : team,
      ),
    );
  };

  const getLeadInitial = (name) => (name ? name.charAt(0).toUpperCase() : "?");
  const getInitialColor = (index) => {
    const colors = [
      "bg-orange-400",
      "bg-yellow-400",
      "bg-purple-400",
      "bg-pink-400",
      "bg-blue-400",
    ];
    return colors[index % colors.length];
  };
  const toggleEmployeeSelection = (id) => {
    if (selectedEmployees.includes(id))
      setSelectedEmployees(selectedEmployees.filter((i) => i !== id));
    else setSelectedEmployees([...selectedEmployees, id]);
  };
  const toggleLeadSelection = (id) => {
    if (selectedLeadIds.includes(id))
      setSelectedLeadIds(selectedLeadIds.filter((i) => i !== id));
    else setSelectedLeadIds([...selectedLeadIds, id]);
  };
  const toggleSelectAllLeads = () => {
    if (selectedLeadIds.length === filteredLeads.length) setSelectedLeadIds([]);
    else setSelectedLeadIds(filteredLeads.map((l) => l.id));
  };
  const hasActiveFilters = Object.values(activeFilters).some(
    (v) => v && v !== "created",
  );

  // ── Disposition Cards ──────────────────────────────────────────────────────
  const DispositionCards = ({ data = stats }) =>
    data.dispositions?.length > 0 ? (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-3">
        <div
          className={`border rounded-xl p-3 hover:shadow-lg transition-all ${isDark ? "bg-[#111827] border-white/10" : "bg-white border-gray-200"}`}
        >
          <div className="flex items-center space-x-1 mb-1">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
          </div>
          <div className="text-xl font-black text-blue-600">
            {data.totalLeads}
          </div>
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1 leading-tight">
            Total Leads
          </div>
          <div className="text-[9px] font-black text-gray-500 mt-0.5">
            Untagged: {data.untaggedLeads}
          </div>
        </div>
        {data.dispositions.map((d) => (
          <div
            key={d.dispositionId}
            className={`border rounded-xl p-3 hover:shadow-lg transition-all ${isDark ? "bg-[#111827] border-white/10" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-center space-x-1 mb-1">
              <div
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: currentTheme.color }}
              ></div>
            </div>
            <div
              className="text-xl font-black"
              style={{ color: currentTheme.color }}
            >
              {d.count}
            </div>
            <div
              className={`text-[10px] font-black mt-1 leading-tight truncate uppercase tracking-widest ${isDark ? "text-gray-300" : "text-gray-600"}`}
              title={d.dispositionName}
            >
              {d.dispositionName}
            </div>
            <div className="text-[9px] font-black text-gray-400 mt-0.5">
              {d.percentage}%
            </div>
          </div>
        ))}
      </div>
    ) : null;

  // ── Tab list ───────────────────────────────────────────────────────────────
  const tabList = [
    "Fresh",
    "Assigned",
    ...(activeFilters.entryDate ? ["Entry Leads"] : []),
  ];

  // ── Reset filters helper ───────────────────────────────────────────────────
  const resetFilters = {
    team: "",
    source: "",
    dateType: "created",
    assignedDate: "today",
    agent: "",
    category: "",
    status: "",
    product: "",
    campaign: "",
    limit: "10",
    customStartDate: "",
    customEndDate: "",
    dispositionId: "",
    entryDate: "",
    floorId: "", // ✅ include in reset
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      className="space-y-6 min-h-screen p-6 transition-colors duration-300"
      style={{ backgroundColor: currentTheme.color + "12" }}
    >
      <BulkLeadUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onUploadSuccess={handleUploadSuccess}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2
          className={`text-2xl font-black uppercase tracking-widest ${isDark ? "text-white" : "text-gray-800"}`}
        >
          Leads ({stats.totalLeads})
        </h2>
        <div className="flex items-center space-x-3">
          {(has("LEAD_CATEGORY_FILTER") || has("LEAD_STATUS_FILTER")) && (
            <button
              onClick={() => {
                setTempFilters(activeFilters);
                setIsFilterModalOpen(true);
              }}
              className={`p-2 border rounded-lg transition-colors ${hasActiveFilters ? "bg-opacity-10" : "border-gray-300 hover:bg-gray-50 text-gray-600"}`}
              style={{
                backgroundColor: hasActiveFilters
                  ? currentTheme.color + "1A"
                  : undefined,
                borderColor: hasActiveFilters ? currentTheme.color : undefined,
                color: hasActiveFilters ? currentTheme.color : undefined,
              }}
            >
              <Filter className="w-5 h-5" />
            </button>
          )}
          {has("LEAD_CREATE") && (
            <button
              onClick={() => setShowAddModal(true)}
              className="p-2 text-white rounded-lg transition-colors shadow-lg active:scale-95"
              style={{ backgroundColor: currentTheme.color }}
            >
              <Plus className="w-5 h-5" />
            </button>
          )}
          {has("LEAD_DATA_EXPORT") && (
            <ExcelExportButton
              onClick={handleExportExcel}
              isDownloading={isDownloading}
              label="Excel"
              className="!px-3 !py-2 !rounded-lg shadow-lg active:scale-95"
            />
          )}
        </div>
      </div>

      {/* Main Tabs */}
      <div
        className={`flex items-center space-x-6 border-b transition-all mb-6 ${isDark ? "border-white/5" : "border-gray-200"}`}
      >
        {["Dashboard", "Leads"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveMainTab(tab)}
            className={`pb-3 text-xs font-black uppercase tracking-[0.2em] transition-all relative ${activeMainTab === tab ? "" : isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-700"}`}
            style={{
              color: activeMainTab === tab ? currentTheme.color : undefined,
            }}
          >
            {tab}
            {activeMainTab === tab && (
              <div
                className="absolute bottom-0 left-0 w-full h-1 rounded-t-full"
                style={{
                  backgroundColor: currentTheme.color,
                  boxShadow: `0 -4px 10px ${currentTheme.color}66`,
                }}
              ></div>
            )}
          </button>
        ))}
      </div>

      {/* ── Dashboard Tab ── */}
      {activeMainTab === "Dashboard" && (
        <div className="space-y-6">
          <div className="flex flex-col xl:flex-row gap-6">
            <div className="w-full">
              <DispositionCards data={stats} />
            </div>
            <div
              className={`w-full xl:w-64 flex-shrink-0 rounded-2xl border p-6 flex flex-col justify-center min-h-[178px] transition-all shadow-sm ${isDark ? "bg-[#111827] border-white/10" : "bg-white border-gray-200"}`}
            >
              <div className="flex flex-col items-center justify-end gap-4">
                <div className="w-full text-center rounded-lg">
                  <p
                    className={`text-xs font-black uppercase tracking-widest mb-2 ${isDark ? "text-gray-400" : "text-[#1B3E38]/60"}`}
                  >
                    Target:
                  </p>
                  <p
                    className={`text-2xl font-black ${isDark ? "text-white" : "text-[#1B3E38]"}`}
                  >
                    ₹ N/A
                  </p>
                </div>
                <div className="w-full text-center rounded-lg">
                  <p
                    className={`text-xs font-black uppercase tracking-widest mb-2 ${isDark ? "text-gray-400" : "text-[#1B3E38]/60"}`}
                  >
                    Total Revenue:
                  </p>
                  <p
                    className={`text-2xl font-black ${isDark ? "text-white" : "text-[#1B3E38]"}`}
                  >
                    ₹ N/A
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Team Tabs */}
          <div
            className={`flex items-center space-x-4 border-b overflow-x-auto pb-2 transition-all ${isDark ? "border-white/5" : "border-gray-200"}`}
            style={{ scrollbarWidth: "thin" }}
          >
            {teams.length > 0 ? (
              teams.map((team) => (
                <button
                  key={team.id}
                  onClick={() => {
                    setSelectedTeam(team.name);
                    setSelectedTeamId(team.id);
                    fetchTeamStats(team.id);
                  }}
                  className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap flex-shrink-0 relative ${selectedTeamId === team.id ? "" : isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-700"}`}
                  style={{
                    color:
                      selectedTeamId === team.id
                        ? currentTheme.color
                        : undefined,
                  }}
                >
                  {team.name}
                  {selectedTeamId === team.id && (
                    <div
                      className="absolute bottom-0 left-0 w-full h-0.5 rounded-t-full"
                      style={{ backgroundColor: currentTheme.color }}
                    ></div>
                  )}
                </button>
              ))
            ) : (
              <div className="text-sm text-gray-500 px-4 py-2">
                Loading teams...
              </div>
            )}
          </div>

          {/* Team Stats */}
          <div className="flex flex-col xl:flex-row gap-6">
            {teamStatsLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-grow">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className={`rounded-xl p-4 animate-pulse border ${isDark ? "bg-white/5 border-white/5" : "bg-white border-gray-100"}`}
                  >
                    <div
                      className={`h-8 rounded mb-2 ${isDark ? "bg-white/10" : "bg-gray-200"}`}
                    ></div>
                    <div
                      className={`h-4 rounded ${isDark ? "bg-white/5" : "bg-gray-200"}`}
                    ></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="w-full">
                <DispositionCards data={teamStats} />
              </div>
            )}
            <div
              className={`w-full xl:w-64 flex-shrink-0 rounded-2xl border p-6 flex flex-col justify-center min-h-[178px] transition-all shadow-sm ${isDark ? "bg-[#111827] border-white/10" : "bg-white border-gray-200"}`}
            >
              <div className="flex flex-col items-center justify-end gap-4">
                <div className="w-full text-center">
                  <p
                    className={`text-xs font-black uppercase tracking-widest mb-2 ${isDark ? "text-gray-400" : "text-[#1B3E38]/60"}`}
                  >
                    Target:
                  </p>
                  <p
                    className={`text-2xl font-black ${isDark ? "text-white" : "text-[#1B3E38]"}`}
                  >
                    ₹ N/A
                  </p>
                </div>
                <div className="w-full text-center">
                  <p
                    className={`text-xs font-black uppercase tracking-widest mb-2 ${isDark ? "text-gray-400" : "text-[#1B3E38]/60"}`}
                  >
                    Total Revenue:
                  </p>
                  <p
                    className={`text-2xl font-black ${isDark ? "text-white" : "text-[#1B3E38]"}`}
                  >
                    ₹ N/A
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Top Agents */}
          {/* <div>
            <h3 className={`text-lg font-black uppercase tracking-widest mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Agents</h3>
            {topAgents.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {topAgents.map(agent => (
                  <div key={agent.id} className={`rounded-2xl border p-5 hover:shadow-xl transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-200 shadow-sm'}`}>
                    <div className="flex items-center space-x-3 mb-3">
                      <img src={agent.avatar} alt={agent.name} className="w-12 h-12 rounded-full border-2 border-white/10" />
                      <div className="flex-1 min-w-0">
                        <p className={`font-black text-sm truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>{agent.name}</p>
                        <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Emp: {agent.employeeId}</p>
                      </div>
                    </div>
                    <div className="text-center pt-2 border-t border-white/5">
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Orders</p>
                      <p className={`text-2xl font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>{agent.orders}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No agent data available</p>
              </div>
            )}
          </div> */}
        </div>
      )}

      {/* ── Leads Tab ── */}
      {activeMainTab === "Leads" && (
        <div className="space-y-6">
          <DispositionCards data={stats} />

          {/* Fresh / Assigned / Entry Leads Tabs */}
          <div className="flex items-center space-x-4 mb-4 flex-wrap gap-y-2">
            <div className="flex items-center space-x-4">
              {tabList.map((tab) => {
                const count =
                  tab === "Fresh"
                    ? freshCount
                    : tab === "Assigned"
                      ? assignedCount
                      : entryLeadsData.totalEntryCount;
                return (
                  <button
                    key={tab}
                    onClick={() => {
                      setActiveTab(tab);
                      setActiveStatusTab("");
                      setSelectedLeadIds([]);
                      setCurrentPage(1);
                      if (tab === "Entry Leads") {
                        fetchEntryLeads(
                          activeFilters.entryDate,
                          activeSourceFilter,
                          activeAssignFilter,
                          0,
                        );
                      } else {
                        const query = buildQuery(
                          { ...activeFilters, status: "", dispositionId: "" },
                          tab,
                          teams,
                        );
                        fetchLeads(query, tab);
                      }
                    }}
                    className={`px-6 py-2.5 rounded-2xl transition-all font-black text-[10px] uppercase tracking-widest flex items-center space-x-2 active:scale-95 border-2 ${
                      activeTab === tab
                        ? "text-white shadow-2xl scale-105 z-10"
                        : isDark
                          ? "text-gray-500 bg-white/5 border-transparent hover:text-gray-300 hover:bg-white/10"
                          : "text-gray-500 bg-gray-50 border-transparent hover:bg-gray-100"
                    }`}
                    style={{
                      backgroundColor:
                        activeTab === tab ? currentTheme.color : undefined,
                      borderColor:
                        activeTab === tab ? currentTheme.color : undefined,
                      boxShadow:
                        activeTab === tab
                          ? `0 10px 25px -5px ${currentTheme.color}40`
                          : undefined,
                    }}
                  >
                    <span>{tab}</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[9px] font-black ${activeTab === tab ? "bg-white text-gray-900 shadow-sm" : isDark ? "bg-white/10 text-gray-400" : "bg-gray-200 text-gray-600"}`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}

              {/* Clear Entry Date button */}
              {activeFilters.entryDate && (
                <button
                  onClick={() => {
                    setActiveFilters({ ...activeFilters, entryDate: "" });
                    setTempFilters({ ...tempFilters, entryDate: "" });
                    setActiveTab("Fresh");
                  }}
                  className={`px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center space-x-1 border transition-all ${isDark ? "border-white/10 text-gray-400 hover:text-red-400" : "border-gray-200 text-gray-500 hover:text-red-500"}`}
                >
                  <X className="w-3 h-3" />
                  <span>
                    {dayjs(activeFilters.entryDate).isValid()
                      ? dayjs(activeFilters.entryDate).format("DD-MM-YYYY")
                      : activeFilters.entryDate}
                  </span>
                </button>
              )}

              {/* ✅ Active Floor indicator */}
              {activeFilters.floorId && (
                <button
                  onClick={() => {
                    setActiveFilters({ ...activeFilters, floorId: "" });
                    setTempFilters({ ...tempFilters, floorId: "" });
                  }}
                  className={`px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center space-x-1 border transition-all ${isDark ? "border-white/10 text-gray-400 hover:text-red-400" : "border-gray-200 text-gray-500 hover:text-red-500"}`}
                >
                  <X className="w-3 h-3" />
                  <span>
                    Floor:{" "}
                    {floors.find(
                      (f) => String(f.id) === String(activeFilters.floorId),
                    )?.name || activeFilters.floorId}
                  </span>
                </button>
              )}
            </div>

            {/* Controls — only for Fresh/Assigned */}
            {activeTab !== "Entry Leads" && (
              <div className="flex items-center space-x-3 ml-auto w-full">
                <ThemeSelect
                  label="Selection:"
                  hideLabelOnSelect
                  allowCustomInput
                  placeholder="Count"
                  value={
                    selectedLeadIds.length === 0
                      ? "None"
                      : selectedLeadIds.length === filteredLeads.length &&
                          filteredLeads.length > 0
                        ? `All (${filteredLeads.length})`
                        : selectedLeadIds.length
                  }
                  onChange={(val) => {
                    if (
                      typeof val === "string" &&
                      val.toLowerCase().startsWith("all")
                    )
                      setSelectedLeadIds(filteredLeads.map((l) => l.id));
                    else if (val === "None" || val === "")
                      setSelectedLeadIds([]);
                    else {
                      const c =
                        typeof val === "string"
                          ? val.replace(/[^\d]/g, "")
                          : val;
                      if (c && !isNaN(c))
                        setSelectedLeadIds(
                          filteredLeads
                            .slice(
                              0,
                              Math.min(parseInt(c) || 0, filteredLeads.length),
                            )
                            .map((l) => l.id),
                        );
                    }
                  }}
                  options={[
                    { label: "All", value: "All" },
                    { label: "10", value: "10" },
                    { label: "20", value: "20" },
                    { label: "50", value: "50" },
                    { label: "100", value: "100" },
                    { label: "None", value: "None" },
                  ]}
                  className="w-auto"
                />

                <ThemeSelect
                  label="Show Entries:"
                  hideLabelOnSelect
                  value={
                    activeFilters.limit === "all"
                      ? `All (${totalMatches})`
                      : activeFilters.limit
                  }
                  onChange={(val) => {
                    const newLimit =
                      typeof val === "string" &&
                      val.toLowerCase().startsWith("all")
                        ? "all"
                        : val;
                    setActiveFilters({ ...activeFilters, limit: newLimit });
                    setCurrentPage(1);
                    if (limitDebounceRef.current)
                      clearTimeout(limitDebounceRef.current);
                    limitDebounceRef.current = setTimeout(() => {
                      const updatedFilters = {
                        ...activeFilters,
                        limit: newLimit,
                      };
                      const query = buildQuery(
                        updatedFilters,
                        activeTab,
                        teams,
                        0,
                      );
                      fetchLeads(query, activeTab);
                    }, 600);
                  }}
                  options={[
                    { label: "10", value: "10" },
                    { label: "25", value: "25" },
                    { label: "50", value: "50" },
                    { label: "100", value: "100" },
                    { label: "All", value: "all" },
                  ]}
                  className="w-auto"
                />

                <ThemeSelect
                  label="Source:"
                  hideLabelOnSelect
                  value={activeFilters.source}
                  onChange={(val) =>
                    setActiveFilters({ ...activeFilters, source: val })
                  }
                  options={dynamicSourceOptions}
                  className="w-auto"
                />

                {/* Date Range Popover */}
                <div className="relative" ref={datePopoverRef}>
                  <button
                    onClick={() => setIsDatePopoverOpen(!isDatePopoverOpen)}
                    className="flex items-center bg-white border border-gray-300 rounded-full px-4 py-1.5 shadow-sm cursor-pointer transition-all hover:bg-gray-50 h-[38px]"
                    style={{
                      borderColor: isDatePopoverOpen
                        ? currentTheme.color
                        : "#D1D5DB",
                    }}
                  >
                    <CalendarDays className="w-5 h-5 text-orange-400 mr-2" />
                    <span className="text-[15px] font-medium text-gray-500">
                      {getDateRangeLabel()}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 ml-2 transition-transform ${isDatePopoverOpen ? "rotate-180" : ""}`}
                      style={{ color: currentTheme.color }}
                    />
                  </button>
                  {isDatePopoverOpen && (
                    <div
                      className={`absolute left-0 mt-2 rounded-2xl shadow-xl border p-3 z-[100] w-56 ${isDark ? "bg-[#111827] border-white/10" : "bg-white border-gray-100"}`}
                    >
                      <div className="flex flex-col space-y-1">
                        {[
                          { label: "All View", value: "" },
                          { label: "Today", value: "today" },
                          { label: "Yesterday", value: "yesterday" },
                          { label: "This Week", value: "this_week" },
                          { label: "This Month", value: "this_month" },
                          { label: "This Year", value: "this_year" },
                          { label: "Custom Range", value: "custom" },
                        ].map((opt) => {
                          const isSelected =
                            activeFilters.assignedDate === opt.value;
                          return (
                            <button
                              key={opt.value}
                              onClick={() => {
                                if (opt.value === "custom") {
                                  setIsDatePopoverOpen(false);
                                  setShowCustomDateModal(true);
                                } else {
                                  setActiveFilters({
                                    ...activeFilters,
                                    assignedDate: opt.value,
                                    customStartDate: "",
                                    customEndDate: "",
                                  });
                                  setCurrentPage(1);
                                  setIsDatePopoverOpen(false);
                                }
                              }}
                              className="w-full px-5 py-2.5 text-left text-[14px] transition-colors rounded-xl font-medium"
                              style={{
                                backgroundColor: isSelected
                                  ? `${currentTheme.color}15`
                                  : "transparent",
                                color: isSelected
                                  ? currentTheme.color
                                  : isDark
                                    ? "#D1D5DB"
                                    : "#374151",
                                fontWeight: isSelected ? "bold" : "normal",
                              }}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="relative">
                  <ExpandableSearchBar
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    placeholder="Search by lead id or customer name"
                  />
                </div>
              </div>
            )}
          </div>

          {/* ══════════════════════════════════════════════════════════
              ENTRY LEADS TAB CONTENT
          ══════════════════════════════════════════════════════════ */}
          {activeTab === "Entry Leads" && activeFilters.entryDate && (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  {
                    label: "Total Entry",
                    value: entryLeadsData.totalEntryCount,
                    colorClass: "text-blue-500",
                    bgClass: isDark ? "bg-blue-500/10" : "bg-blue-50",
                  },
                  {
                    label: "Assigned",
                    value: entryLeadsData.assignedCount,
                    colorClass: "text-green-500",
                    bgClass: isDark ? "bg-green-500/10" : "bg-green-50",
                  },
                  {
                    label: "Unassigned",
                    value: entryLeadsData.unassignedCount,
                    colorClass: "text-orange-500",
                    bgClass: isDark ? "bg-orange-500/10" : "bg-orange-50",
                  },
                  {
                    label: "Showing",
                    value: entryLeadsData.totalCount,
                    colorClass: "text-purple-500",
                    bgClass: isDark ? "bg-purple-500/10" : "bg-purple-50",
                  },
                ].map((card) => (
                  <div
                    key={card.label}
                    className={`rounded-xl p-4 border ${isDark ? "border-white/10" : "border-gray-200"} ${card.bgClass}`}
                  >
                    <div className={`text-3xl font-black ${card.colorClass}`}>
                      {card.value}
                    </div>
                    <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">
                      {card.label}
                    </div>
                    {card.label === "Total Entry" && (
                      <div className="text-[9px] text-gray-500 mt-0.5">
                        {dayjs(activeFilters.entryDate).isValid()
                          ? dayjs(activeFilters.entryDate).format("DD-MM-YYYY")
                          : activeFilters.entryDate}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Assign / Unassign Filter Pills */}
              <div className="flex items-center space-x-2">
                <span
                  className={`text-[10px] font-black uppercase tracking-widest ${isDark ? "text-gray-400" : "text-gray-500"}`}
                >
                  View:
                </span>
                {[
                  {
                    label: "All",
                    value: "",
                    count: entryLeadsData.totalEntryCount,
                  },
                  {
                    label: "Assigned",
                    value: "assigned",
                    count: entryLeadsData.assignedCount,
                  },
                  {
                    label: "Unassigned",
                    value: "unassigned",
                    count: entryLeadsData.unassignedCount,
                  },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setActiveAssignFilter(opt.value);
                      setEntryCurrentPage(1);
                      fetchEntryLeads(
                        activeFilters.entryDate,
                        activeSourceFilter,
                        opt.value,
                        0,
                      );
                    }}
                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center space-x-1.5 ${
                      activeAssignFilter === opt.value
                        ? "text-white shadow-lg"
                        : isDark
                          ? "text-gray-400 bg-white/5 border border-white/10 hover:text-white hover:bg-white/10"
                          : "text-gray-600 bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    style={{
                      backgroundColor:
                        activeAssignFilter === opt.value
                          ? currentTheme.color
                          : undefined,
                      boxShadow:
                        activeAssignFilter === opt.value
                          ? `0 4px 12px ${currentTheme.color}40`
                          : undefined,
                    }}
                  >
                    <span>{opt.label}</span>
                    <span
                      className={`px-1.5 py-0.5 rounded-full text-[9px] font-black ${activeAssignFilter === opt.value ? "bg-white text-gray-900" : isDark ? "bg-white/10 text-gray-400" : "bg-gray-100 text-gray-600"}`}
                    >
                      {opt.count}
                    </span>
                  </button>
                ))}
              </div>

              {/* Source-wise Filter */}
              {entryLeadsData.sourceWiseCounts.length > 0 && (
                <div className="space-y-2">
                  <span
                    className={`text-[10px] font-black uppercase tracking-widest ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Source:
                  </span>
                  <div
                    className="flex items-center space-x-2 overflow-x-auto pb-1"
                    style={{ scrollbarWidth: "thin" }}
                  >
                    <button
                      onClick={() => {
                        setActiveSourceFilter("");
                        setEntryCurrentPage(1);
                        fetchEntryLeads(
                          activeFilters.entryDate,
                          "",
                          activeAssignFilter,
                          0,
                        );
                      }}
                      className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest whitespace-nowrap flex items-center space-x-1.5 transition-all ${
                        activeSourceFilter === ""
                          ? "text-white shadow-lg"
                          : isDark
                            ? "text-gray-400 bg-white/5 border border-white/10 hover:text-white hover:bg-white/10"
                            : "text-gray-600 bg-white border border-gray-200 hover:bg-gray-50"
                      }`}
                      style={{
                        backgroundColor:
                          activeSourceFilter === ""
                            ? currentTheme.color
                            : undefined,
                        boxShadow:
                          activeSourceFilter === ""
                            ? `0 4px 12px ${currentTheme.color}40`
                            : undefined,
                      }}
                    >
                      <span>All Sources</span>
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-[9px] font-black ${activeSourceFilter === "" ? "bg-white text-gray-900" : isDark ? "bg-white/10 text-gray-400" : "bg-gray-100 text-gray-600"}`}
                      >
                        {entryLeadsData.totalEntryCount}
                      </span>
                    </button>

                    {entryLeadsData.sourceWiseCounts.map((src) => (
                      <button
                        key={src.source}
                        onClick={() => {
                          setActiveSourceFilter(src.source);
                          setEntryCurrentPage(1);
                          fetchEntryLeads(
                            activeFilters.entryDate,
                            src.source,
                            activeAssignFilter,
                            0,
                          );
                        }}
                        className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest whitespace-nowrap flex items-center space-x-1.5 transition-all ${
                          activeSourceFilter === src.source
                            ? "text-white shadow-lg"
                            : isDark
                              ? "text-gray-400 bg-white/5 border border-white/10 hover:text-white hover:bg-white/10"
                              : "text-gray-600 bg-white border border-gray-200 hover:bg-gray-50"
                        }`}
                        style={{
                          backgroundColor:
                            activeSourceFilter === src.source
                              ? currentTheme.color
                              : undefined,
                          boxShadow:
                            activeSourceFilter === src.source
                              ? `0 4px 12px ${currentTheme.color}40`
                              : undefined,
                        }}
                      >
                        <span>{src.source}</span>
                        <span
                          className={`px-1.5 py-0.5 rounded-full text-[9px] font-black ${activeSourceFilter === src.source ? "bg-white text-gray-900" : isDark ? "bg-white/10 text-gray-400" : "bg-gray-100 text-gray-600"}`}
                        >
                          {src.count}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Entry Leads Table */}
              {entryLeadsLoading ? (
                <div className="text-center py-12">
                  <div
                    className="inline-block w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: currentTheme.color }}
                  ></div>
                  <p
                    className={`mt-3 text-sm font-black uppercase tracking-widest ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Loading Entry Leads...
                  </p>
                </div>
              ) : entryLeadsData.leads.length > 0 ? (
                <div
                  className={`rounded-2xl shadow-lg overflow-hidden border transition-all ${isDark ? "bg-[#111827] border-white/10" : "bg-white border-gray-200"}`}
                >
                  <div
                    className="overflow-x-auto overflow-y-auto"
                    style={{ maxHeight: "600px", scrollbarWidth: "thin" }}
                  >
                    <table className="w-full min-w-[900px]">
                      <thead
                        className="text-white sticky top-0 z-10"
                        style={{ background: currentTheme.color }}
                      >
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                            Customer Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                            Contact
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                            Lead ID
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                            Source
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                            Assigned To
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                            Team
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                            Created At
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                            Remarks
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                            History
                          </th>
                        </tr>
                      </thead>
                      <tbody
                        className={`divide-y transition-colors ${isDark ? "divide-white/5" : "divide-gray-100"}`}
                      >
                        {entryLeadsData.leads.map((lead, index) => (
                          <tr
                            key={lead.id}
                            className={`transition-colors ${isDark ? "hover:bg-white/5" : "hover:bg-gray-50"}`}
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                <div
                                  className={`w-8 h-8 rounded-full ${getInitialColor(index)} flex items-center justify-center text-white font-black text-xs flex-shrink-0`}
                                >
                                  {getLeadInitial(lead.customerName)}
                                </div>
                                <span
                                  className={`text-sm font-black truncate max-w-[150px] ${isDark ? "text-gray-200" : "text-gray-900"}`}
                                >
                                  {lead.customerName || "N/A"}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span
                                className={`text-sm font-black ${isDark ? "text-gray-400" : "text-gray-700"}`}
                              >
                                {lead.contactNumber || "N/A"}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span
                                className={`text-sm font-black ${isDark ? "text-gray-400" : "text-gray-700"}`}
                              >
                                {lead.leadId || lead.id || "N/A"}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span
                                className={`text-sm font-black ${isDark ? "text-gray-400" : "text-gray-700"}`}
                              >
                                {lead.leadSourceRaw || lead.source || "N/A"}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span
                                className={`text-[10px] font-black px-2 py-1 rounded-full border ${isDark ? "bg-white/5 border-white/10 text-gray-300" : "bg-gray-50 border-gray-100 text-gray-700"}`}
                              >
                                {lead.status || "N/A"}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span
                                className={`text-sm font-black ${lead.assignedToName ? (isDark ? "text-green-400" : "text-green-600") : isDark ? "text-orange-400" : "text-orange-500"}`}
                              >
                                {lead.assignedToName || "Unassigned"}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span
                                className={`text-sm font-black ${isDark ? "text-gray-400" : "text-gray-600"}`}
                              >
                                {lead.teamName || "N/A"}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span
                                className={`text-xs font-black ${isDark ? "text-gray-400" : "text-gray-600"}`}
                              >
                                {formatDate(lead.createdAt)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <p
                                className={`text-xs font-black truncate max-w-xs ${isDark ? "text-gray-500" : "text-gray-600"}`}
                              >
                                {lead.remarks || "—"}
                              </p>
                            </td>
                            <td className="px-4 py-3 text-center whitespace-nowrap">
                              <button
                                onClick={() => {
                                  setSelectedHistoryLead(lead);
                                  setShowHistoryModal(true);
                                }}
                                className="p-1.5 rounded-lg text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 transition-colors inline-flex items-center"
                                title="View Lead History"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    currentPage={entryCurrentPage}
                    totalItems={entryLeadsData.totalCount}
                    itemsPerPage={parseInt(
                      activeFilters.limit === "all"
                        ? "500"
                        : activeFilters.limit || "20",
                    )}
                    onPageChange={(page) => {
                      setEntryCurrentPage(page);
                      fetchEntryLeads(
                        activeFilters.entryDate,
                        activeSourceFilter,
                        activeAssignFilter,
                        page - 1,
                      );
                    }}
                  />
                </div>
              ) : (
                <div
                  className={`rounded-2xl border p-12 text-center ${isDark ? "bg-[#111827] border-white/10" : "bg-white border-gray-200"}`}
                >
                  <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p
                    className={`text-lg font-black ${isDark ? "text-gray-300" : "text-gray-700"}`}
                  >
                    No leads found
                  </p>
                  <p
                    className={`text-sm mt-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    {dayjs(activeFilters.entryDate).isValid()
                      ? dayjs(activeFilters.entryDate).format("DD-MM-YYYY")
                      : activeFilters.entryDate}{" "}
                    —{" "}
                    {activeSourceFilter ? `Source: ${activeSourceFilter}` : ""}
                    {activeAssignFilter ? ` | ${activeAssignFilter}` : ""}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════
              FRESH / ASSIGNED TAB CONTENT
          ══════════════════════════════════════════════════════════ */}
          {(activeTab === "Fresh" || activeTab === "Assigned") && (
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1 w-full lg:w-auto overflow-hidden">
                {/* Status + Disposition Tabs */}
                {activeTab === "Assigned" && has("LEAD_STATUS_FILTER") && (
                  <div className="flex items-center space-x-2 mb-4 overflow-x-auto pb-2">
                    <button
                      onClick={() => handleStatusTabClick("")}
                      className={`px-4 py-2 whitespace-nowrap text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-sm flex items-center space-x-2 ${activeStatusTab === "" ? "text-white shadow-lg" : isDark ? "text-gray-400 bg-white/5 border border-white/10" : "text-gray-600 bg-white border border-gray-200"}`}
                      style={{
                        backgroundColor:
                          activeStatusTab === ""
                            ? currentTheme.color
                            : undefined,
                        boxShadow:
                          activeStatusTab === ""
                            ? `0 4px 12px ${currentTheme.color}40`
                            : undefined,
                      }}
                    >
                      <span>All</span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[9px] font-black ${activeStatusTab === "" ? "bg-white text-gray-900 shadow-sm" : isDark ? "bg-white/10 text-gray-400" : "bg-gray-200 text-gray-600"}`}
                      >
                        {assignedCount}
                      </span>
                    </button>

                    <div
                      className="px-4 py-2 whitespace-nowrap text-[10px] font-black uppercase tracking-widest rounded-xl shadow-sm flex items-center space-x-2 text-white"
                      style={{
                        backgroundColor: currentTheme.color,
                        boxShadow: `0 4px 12px ${currentTheme.color}40`,
                      }}
                    >
                      <span>Total Disposed</span>
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-white text-gray-900 shadow-sm">
                        {stats.dispositions?.reduce(
                          (acc, d) => acc + (d.count || 0),
                          0,
                        ) || 0}
                      </span>
                    </div>

                    {stats.dispositions?.map((d) => (
                      <button
                        key={`disp-${d.dispositionId}`}
                        onClick={() =>
                          handleStatusTabClick(
                            `DISP_${d.dispositionId}`,
                            d.dispositionId,
                          )
                        }
                        className={`px-4 py-2 whitespace-nowrap text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-sm flex items-center space-x-2 ${activeStatusTab === `DISP_${d.dispositionId}` ? "text-white shadow-lg" : isDark ? "text-gray-400 bg-white/5 border border-white/10" : "text-gray-600 bg-white border border-gray-200"}`}
                        style={{
                          backgroundColor:
                            activeStatusTab === `DISP_${d.dispositionId}`
                              ? currentTheme.color
                              : undefined,
                          boxShadow:
                            activeStatusTab === `DISP_${d.dispositionId}`
                              ? `0 4px 12px ${currentTheme.color}40`
                              : undefined,
                        }}
                      >
                        <span>{d.dispositionName}</span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[9px] font-black ${activeStatusTab === `DISP_${d.dispositionId}` ? "bg-white text-gray-900 shadow-sm" : isDark ? "bg-white/10 text-gray-400" : "bg-gray-200 text-gray-600"}`}
                        >
                          {d.count}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {loading ? (
                  <div className="text-center py-12">
                    <div
                      className="inline-block w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
                      style={{ borderColor: currentTheme.color }}
                    ></div>
                  </div>
                ) : filteredLeads.length > 0 ? (
                  <>
                    <div
                      className={`rounded-2xl shadow-lg overflow-hidden border transition-all ${isDark ? "bg-[#111827] border-white/10" : "bg-white border-gray-200"}`}
                    >
                      <div
                        className="overflow-x-auto overflow-y-auto"
                        style={{ scrollbarWidth: "thin", maxHeight: "600px" }}
                      >
                        <table className="w-full min-w-[1000px]">
                          <thead
                            className="text-white sticky top-0 z-10"
                            style={{ background: currentTheme.color }}
                          >
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                <button
                                  onClick={toggleSelectAllLeads}
                                  className="flex items-center"
                                >
                                  {selectedLeadIds.length ===
                                    filteredLeads.length &&
                                  filteredLeads.length > 0 ? (
                                    <div className="w-5 h-5 bg-white rounded flex items-center justify-center">
                                      <svg
                                        className="w-3 h-3"
                                        style={{ color: currentTheme.color }}
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </div>
                                  ) : (
                                    <div className="w-5 h-5 border-2 border-white rounded"></div>
                                  )}
                                </button>
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                Customer Name
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                Contact Number
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                Lead ID
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                Source
                              </th>
                              {activeTab === "Assigned" && (
                                <>
                                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                    Status
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                    Assigned To
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                    Employee ID
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                    Updated At
                                  </th>
                                </>
                              )}
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                Created At
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                Last Remarks
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                History
                              </th>
                            </tr>
                          </thead>
                          <tbody
                            className={`divide-y transition-colors ${isDark ? "bg-transparent divide-white/5" : "bg-white divide-gray-100"}`}
                          >
                            {filteredLeads.map((lead, index) => (
                              <tr
                                key={lead.id}
                                onClick={() => toggleLeadSelection(lead.id)}
                                className={`cursor-pointer transition-colors ${selectedLeadIds.includes(lead.id) ? "bg-opacity-10" : "hover:bg-gray-50"}`}
                                style={{
                                  backgroundColor: selectedLeadIds.includes(
                                    lead.id,
                                  )
                                    ? currentTheme.color + "1A"
                                    : undefined,
                                }}
                              >
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleLeadSelection(lead.id);
                                    }}
                                    className="transition-all active:scale-90"
                                  >
                                    {selectedLeadIds.includes(lead.id) ? (
                                      <div
                                        className="w-5 h-5 rounded flex items-center justify-center shadow-lg"
                                        style={{
                                          backgroundColor: currentTheme.color,
                                        }}
                                      >
                                        <svg
                                          className="w-3 h-3 text-white"
                                          fill="currentColor"
                                          viewBox="0 0 20 20"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      </div>
                                    ) : (
                                      <div
                                        className={`w-5 h-5 border-2 rounded transition-all ${isDark ? "border-white/20 bg-white/5" : "border-gray-300 bg-white"}`}
                                      ></div>
                                    )}
                                  </button>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="flex items-center space-x-2">
                                    <div
                                      className={`w-8 h-8 rounded-full ${getInitialColor(index)} flex items-center justify-center text-white font-black text-xs flex-shrink-0 shadow-sm`}
                                    >
                                      {getLeadInitial(lead.customerName)}
                                    </div>
                                    <span
                                      className={`text-sm font-black truncate max-w-[150px] ${isDark ? "text-gray-200" : "text-gray-900"}`}
                                    >
                                      {lead.customerName || "N/A"}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span
                                    className={`text-sm font-black ${isDark ? "text-gray-400" : "text-gray-700"}`}
                                  >
                                    {lead.contactNumber || "N/A"}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span
                                    className={`text-sm font-black ${isDark ? "text-gray-400" : "text-gray-700"}`}
                                  >
                                    {lead.id || "N/A"}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span
                                    className={`text-sm font-black ${isDark ? "text-gray-400" : "text-gray-700"}`}
                                  >
                                    {lead.leadSourceRaw || "N/A"}
                                  </span>
                                </td>
                                {activeTab === "Assigned" && (
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span
                                      className={`text-[10px] font-black px-2 py-1 rounded-full border ${isDark ? "bg-white/5 border-white/10 text-gray-300" : "bg-gray-50 border-gray-100 text-gray-700"}`}
                                    >
                                      {lead.dispositionName?.replace(
                                        /_/g,
                                        " ",
                                      ) || "N/A"}
                                    </span>
                                  </td>
                                )}
                                {activeTab === "Assigned" && (
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span
                                      className={`text-sm font-black ${isDark ? "text-gray-300" : "text-gray-700"}`}
                                    >
                                      {lead.assignedToName || "N/A"}
                                    </span>
                                  </td>
                                )}
                                {activeTab === "Assigned" && (
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span
                                      className={`text-sm font-black ${isDark ? "text-gray-300" : "text-gray-700"}`}
                                    >
                                      {(() => {
                                        const emp = allEmployees.find(
                                          (e) =>
                                            String(e.id) ===
                                            String(lead.assignedToId),
                                        );
                                        return (
                                          emp?.employeeId ||
                                          lead.assignedToEmployeeId ||
                                          lead.assignedToEmpId ||
                                          "N/A"
                                        );
                                      })()}
                                    </span>
                                  </td>
                                )}
                                {activeTab === "Assigned" && (
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span
                                      className={`text-xs font-black ${isDark ? "text-gray-400" : "text-gray-600"}`}
                                    >
                                      {formatDate(
                                        lead.updatedAt ||
                                          lead.lastUpdated ||
                                          lead.modifiedAt,
                                      )}
                                    </span>
                                  </td>
                                )}
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span
                                    className={`text-xs font-black ${isDark ? "text-gray-400" : "text-gray-600"}`}
                                  >
                                    {formatDate(
                                      lead.createdAt ||
                                        lead.date ||
                                        lead.createdDate,
                                    )}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <p
                                    className={`text-xs font-black truncate max-w-xs ${isDark ? "text-gray-500" : "text-gray-600"}`}
                                  >
                                    {lead.remarks}
                                  </p>
                                </td>
                                <td className="px-4 py-3 text-center whitespace-nowrap">
                                  <button
                                    onClick={() => {
                                      setSelectedHistoryLead(lead);
                                      setShowHistoryModal(true);
                                    }}
                                    className="p-1.5 rounded-lg text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 transition-colors inline-flex items-center"
                                    title="View Lead History"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <Pagination
                        currentPage={currentPage}
                        totalItems={totalLeads}
                        itemsPerPage={
                          activeFilters.limit === "all"
                            ? 500
                            : parseInt(activeFilters.limit || "10")
                        }
                        onPageChange={(page) => {
                          setCurrentPage(page);
                          const query = buildQuery(
                            activeFilters,
                            activeTab,
                            teams,
                            page - 1,
                          );
                          fetchLeads(query, activeTab);
                        }}
                      />
                    </div>

                    {selectedLeadIds.length > 0 && (
                      <div
                        className="mt-4 p-3 border rounded-lg flex items-center justify-between"
                        style={{
                          backgroundColor: currentTheme.color + "1A",
                          borderColor: currentTheme.color + "33",
                        }}
                      >
                        <span
                          className="text-sm font-medium"
                          style={{ color: currentTheme.color }}
                        >
                          {selectedLeadIds.length} lead(s) selected
                        </span>
                        <button
                          onClick={() => setSelectedLeadIds([])}
                          className="text-sm font-medium hover:opacity-80"
                          style={{ color: currentTheme.color }}
                        >
                          Clear selection
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-lg font-medium">
                      {activeTab === "Fresh"
                        ? "No fresh leads found"
                        : "No assigned leads found"}
                    </p>
                    <p className="text-sm mt-2">
                      {activeTab === "Fresh"
                        ? "New leads will appear here"
                        : "Try adjusting your filters"}
                    </p>
                  </div>
                )}
              </div>

              {/* ── Assign Panel ── */}
              {has("LEAD_EDIT") && (
                <div className="w-full lg:w-80 flex-shrink-0">
                  <div
                    className={`rounded-2xl border p-6 sticky top-4 transition-all shadow-lg ${isDark ? "bg-[#111827] border-white/10" : "bg-white border-gray-200"}`}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <h3
                        className={`font-black uppercase tracking-widest text-sm ${isDark ? "text-white" : "text-gray-800"}`}
                      >
                        {activeTab === "Assigned"
                          ? "Re-assign to"
                          : "Assign to"}
                      </h3>
                      {selectedEmployees.length > 0 && (
                        <span
                          className="text-[10px] px-2 py-1 rounded-full font-black uppercase tracking-wider"
                          style={{
                            backgroundColor: currentTheme.color + "1A",
                            color: currentTheme.color,
                          }}
                        >
                          {selectedEmployees.length} selected
                        </span>
                      )}
                    </div>
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {teams.length > 0 ? (
                        teams.map((team) => {
                          const isSelected = selectedTeams.find(
                            (t) => t.id === team.id,
                          );
                          const teamData = selectedTeams.find(
                            (t) => t.id === team.id,
                          );
                          return (
                            <div key={team.id}>
                              <button
                                onClick={() => toggleTeam(team)}
                                className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${isSelected ? "shadow-md scale-[1.02]" : isDark ? "border-white/5 bg-white/5" : "border-gray-100 bg-gray-50/50 hover:border-gray-200"}`}
                                style={{
                                  borderColor: isSelected
                                    ? currentTheme.color
                                    : undefined,
                                  backgroundColor: isSelected
                                    ? currentTheme.color + "1A"
                                    : undefined,
                                }}
                              >
                                <div className="flex items-center space-x-3">
                                  <div
                                    className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? "" : isDark ? "border-white/20" : "border-gray-300"}`}
                                    style={{
                                      backgroundColor: isSelected
                                        ? currentTheme.color
                                        : "transparent",
                                      borderColor: isSelected
                                        ? currentTheme.color
                                        : undefined,
                                    }}
                                  >
                                    {isSelected && (
                                      <svg
                                        className="w-3 h-3 text-white"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    )}
                                  </div>
                                  <span
                                    className={`text-xs font-black uppercase tracking-wider ${isSelected ? (isDark ? "text-white" : "text-gray-900") : isDark ? "text-gray-400" : "text-gray-700"}`}
                                  >
                                    {team.name}
                                  </span>
                                </div>
                                {isSelected &&
                                  teamData?.employees?.length > 0 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleTeamExpansion(team.id);
                                      }}
                                      className="hover:bg-gray-100 rounded p-1"
                                    >
                                      <ChevronDown
                                        className={`w-5 h-5 transition-transform ${teamData.expanded ? "rotate-180" : ""}`}
                                        style={{ color: currentTheme.color }}
                                      />
                                    </button>
                                  )}
                              </button>

                              {isSelected &&
                                teamData?.expanded &&
                                teamData?.employees && (
                                  <div className="ml-4 mt-2 space-y-1">
                                    <div
                                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border mb-2 ${isDark ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-200"}`}
                                    >
                                      <Search className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                      <input
                                        type="text"
                                        placeholder="Search employee..."
                                        value={employeeSearchTerm}
                                        onChange={(e) =>
                                          setEmployeeSearchTerm(e.target.value)
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                        className={`w-full bg-transparent text-xs outline-none placeholder:text-gray-400 ${isDark ? "text-white" : "text-gray-800"}`}
                                      />
                                      {employeeSearchTerm && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEmployeeSearchTerm("");
                                          }}
                                          className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                    {teamData.employees
                                      .filter(
                                        (emp) =>
                                          emp.name
                                            ?.toLowerCase()
                                            .includes(
                                              employeeSearchTerm.toLowerCase(),
                                            ) ||
                                          emp.employeeId
                                            ?.toString()
                                            .includes(employeeSearchTerm),
                                      )
                                      .map((employee) => {
                                        const isSel =
                                          selectedEmployees.includes(
                                            employee.id,
                                          );
                                        return (
                                          <div
                                            key={employee.id}
                                            onClick={() =>
                                              toggleEmployeeSelection(
                                                employee.id,
                                              )
                                            }
                                            className={`flex items-center space-x-3 p-2.5 rounded-xl cursor-pointer transition-all ${isSel ? "shadow-sm" : isDark ? "hover:bg-white/5" : "hover:bg-gray-50"}`}
                                            style={{
                                              backgroundColor: isSel
                                                ? currentTheme.color + "1A"
                                                : undefined,
                                            }}
                                          >
                                            <div
                                              className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isSel ? "" : isDark ? "border-white/20" : "border-gray-300"}`}
                                              style={{
                                                backgroundColor: isSel
                                                  ? currentTheme.color
                                                  : "transparent",
                                                borderColor: isSel
                                                  ? currentTheme.color
                                                  : undefined,
                                              }}
                                            >
                                              {isSel && (
                                                <svg
                                                  className="w-2.5 h-2.5 text-white"
                                                  fill="currentColor"
                                                  viewBox="0 0 20 20"
                                                >
                                                  <path
                                                    fillRule="evenodd"
                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                    clipRule="evenodd"
                                                  />
                                                </svg>
                                              )}
                                            </div>
                                            <img
                                              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(employee.name)}&background=random`}
                                              alt={employee.name}
                                              className="w-8 h-8 rounded-full shadow-sm"
                                            />
                                            <div className="flex-1 min-w-0">
                                              <p
                                                className={`text-xs font-black truncate ${isDark ? (isSel ? "text-white" : "text-gray-300") : "text-gray-800"}`}
                                              >
                                                {employee.name}
                                              </p>
                                              <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest">
                                                Emp: {employee.employeeId}
                                              </p>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    {teamData.employees.filter(
                                      (emp) =>
                                        emp.name
                                          ?.toLowerCase()
                                          .includes(
                                            employeeSearchTerm.toLowerCase(),
                                          ) ||
                                        emp.employeeId
                                          ?.toString()
                                          .includes(employeeSearchTerm),
                                    ).length === 0 && (
                                      <p className="text-xs text-gray-400 text-center py-3">
                                        No employee found
                                      </p>
                                    )}
                                  </div>
                                )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <p className="text-sm">No teams available</p>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={handleAssignClick}
                      disabled={
                        selectedEmployees.length === 0 ||
                        selectedLeadIds.length === 0 ||
                        loading
                      }
                      className="w-full mt-6 py-4 text-white rounded-2xl transition-all font-black text-xs uppercase tracking-[0.2em] shadow-xl active:scale-95 disabled:opacity-30 disabled:grayscale disabled:scale-100 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor:
                          selectedEmployees.length > 0 &&
                          selectedLeadIds.length > 0 &&
                          !loading
                            ? currentTheme.color
                            : undefined,
                      }}
                    >
                      {loading ? (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Processing...</span>
                        </div>
                      ) : (
                        `${activeTab === "Assigned" ? "Re-assign" : "Assign"} ${selectedLeadIds.length > 0 ? `(${selectedLeadIds.length})` : ""}`
                      )}
                    </button>

                    {(selectedEmployees.length > 0 ||
                      selectedLeadIds.length > 0) && (
                      <div
                        className={`mt-4 p-4 rounded-xl text-[10px] font-black uppercase tracking-widest space-y-2 border ${isDark ? "bg-white/5 border-white/5 text-gray-400" : "bg-gray-50 border-gray-100 text-gray-600"}`}
                      >
                        <p className="flex justify-between">
                          <span>Leads:</span>
                          <span style={{ color: currentTheme.color }}>
                            {selectedLeadIds.length}
                          </span>
                        </p>
                        <p className="flex justify-between">
                          <span>Agents:</span>
                          <span style={{ color: currentTheme.color }}>
                            {selectedEmployees.length}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Modals ── */}
      <AddLeadModal
        showAddModal={showAddModal}
        setShowAddModal={setShowAddModal}
        onSuccess={() => {
          fetchLeads();
          fetchDashboardStats();
        }}
      />
      <CampaignSelectionModal
        isOpen={showCampaignModal}
        onClose={() => setShowCampaignModal(false)}
        onSubmit={handleAssignLeads}
        loading={loading}
      />

      {/* ══════════════════════════════════════════════════════════
          FILTER MODAL
      ══════════════════════════════════════════════════════════ */}
      {isFilterModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-[670px] mx-4 overflow-hidden flex flex-col shadow-xl max-h-[100vh]">
            <div
              className="flex items-center justify-between px-6 py-4 border-b"
              style={{ background: currentTheme.color }}
            >
              <h2 className="text-lg font-bold text-white">Filter Leads</h2>
              <button
                onClick={() => setIsFilterModalOpen(false)}
                className="p-1 hover:bg-white/20 rounded"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="grid grid-cols-2 gap-6">
                {/* Lead Source */}
                {has("LEAD_DETAIL_LEADSOURCE_FILTER") && (
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lead Sources
                    </label>
                    <ThemeSelect
                      value={tempFilters.source}
                      onChange={(val) =>
                        setTempFilters({ ...tempFilters, source: val })
                      }
                      options={dynamicSourceOptions}
                      className="w-full"
                    />
                  </div>
                )}

                {/* ✅ Floor Filter */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Floor
                    {tempFilters.floorId && (
                      <button
                        onClick={() =>
                          setTempFilters({ ...tempFilters, floorId: "" })
                        }
                        className="ml-2 text-[10px] text-red-500 hover:text-red-700 font-normal"
                      >
                        Clear
                      </button>
                    )}
                  </label>
                  <select
                    value={tempFilters.floorId}
                    onChange={(e) =>
                      setTempFilters({
                        ...tempFilters,
                        floorId: e.target.value,
                      })
                    }
                    className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 text-sm text-gray-700"
                    style={{ outlineColor: currentTheme.color }}
                  >
                    <option value="">All Floors</option>
                    {floors.map((floor) => (
                      <option key={floor.id} value={floor.id}>
                        {floor.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Entry Date */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Entry Date
                    <span className="ml-2 text-[10px] text-gray-400 font-normal">
                      (Shows Entry Leads tab)
                    </span>
                  </label>
                  <DatePicker
                    value={
                      tempFilters.entryDate
                        ? dayjs(tempFilters.entryDate)
                        : null
                    }
                    onChange={(date) =>
                      setTempFilters({
                        ...tempFilters,
                        entryDate: date ? date.format("YYYY-MM-DD") : "",
                      })
                    }
                    format="DD-MM-YYYY"
                    placeholder="Select Entry Date"
                    className="w-full"
                    allowClear
                    style={{
                      width: "100%",
                      height: "38px",
                      borderRadius: "8px",
                    }}
                  />
                </div>

                {/* Assigned Date */}
                <div className="col-span-1">
                  <DateRangeFilter
                    label="Assigned Date"
                    preset={tempFilters.assignedDate}
                    startDate={tempFilters.customStartDate}
                    endDate={tempFilters.customEndDate}
                    presetOptions={[
                      { label: "All Time", value: "" },
                      { label: "Today", value: "today" },
                      { label: "Yesterday", value: "yesterday" },
                      { label: "This week", value: "this_week" },
                      { label: "Last week", value: "last_week" },
                      { label: "Last 10 days", value: "last_10_days" },
                      { label: "Last 30 days", value: "last_30_days" },
                      { label: "This month", value: "this_month" },
                      { label: "Last month", value: "last_month" },
                      { label: "This year", value: "this_year" },
                      { label: "Last year", value: "last_year" },
                      { label: "Custom Range", value: "custom" },
                    ]}
                    onChange={({ preset, startDate, endDate }) =>
                      setTempFilters({
                        ...tempFilters,
                        assignedDate: preset,
                        customStartDate: startDate,
                        customEndDate: endDate,
                      })
                    }
                  />
                </div>

                {/* Agent Filter */}
                {has("LEAD_DETAIL_AGENT_FILTER") && (
                  <div
                    className={`col-span-1 relative ${isAgentDropdownOpen ? "z-50" : ""}`}
                  >
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      All Agents
                    </label>
                    <div
                      onClick={() =>
                        setIsAgentDropdownOpen(!isAgentDropdownOpen)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between cursor-pointer bg-white"
                    >
                      <span className="text-sm text-gray-700 truncate">
                        {!tempFilters.agent || tempFilters.agent.length === 0
                          ? "All Agents"
                          : `${tempFilters.agent.length} Agent${tempFilters.agent.length > 1 ? "s" : ""} Selected`}
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-500 transition-transform ${isAgentDropdownOpen ? "rotate-180" : ""}`}
                      />
                    </div>
                    {isAgentDropdownOpen && (
                      <div className="absolute top-full left-0 z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden flex flex-col">
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                            <input
                              type="text"
                              placeholder="Search by name or ID"
                              value={agentSearchTerm}
                              onChange={(e) =>
                                setAgentSearchTerm(e.target.value)
                              }
                              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="overflow-y-auto flex-1 p-1">
                          <div
                            onClick={() =>
                              setTempFilters({ ...tempFilters, agent: [] })
                            }
                            className="flex items-center space-x-3 px-3 py-2 hover:bg-gray-50 rounded-md cursor-pointer"
                          >
                            <div
                              className="w-5 h-5 rounded-full flex items-center justify-center border"
                              style={{
                                borderColor: currentTheme.color,
                                backgroundColor:
                                  !tempFilters.agent ||
                                  tempFilters.agent.length === 0
                                    ? currentTheme.color + "1A"
                                    : undefined,
                              }}
                            >
                              {!tempFilters.agent ||
                              tempFilters.agent.length === 0 ? (
                                <Check
                                  className="w-3 h-3"
                                  style={{ color: currentTheme.color }}
                                />
                              ) : (
                                <Plus
                                  className="w-3 h-3"
                                  style={{ color: currentTheme.color }}
                                />
                              )}
                            </div>
                            <span className="text-sm font-medium text-gray-700">
                              All Agents
                            </span>
                          </div>
                          {allEmployees
                            .filter(
                              (emp) =>
                                emp.name
                                  .toLowerCase()
                                  .includes(agentSearchTerm.toLowerCase()) ||
                                (emp.employeeId &&
                                  emp.employeeId
                                    .toString()
                                    .includes(agentSearchTerm)),
                            )
                            .map((emp) => {
                              const isSel =
                                Array.isArray(tempFilters.agent) &&
                                tempFilters.agent.includes(emp.id.toString());
                              return (
                                <div
                                  key={emp.id}
                                  onClick={() => {
                                    const cur = Array.isArray(tempFilters.agent)
                                      ? [...tempFilters.agent]
                                      : [];
                                    setTempFilters({
                                      ...tempFilters,
                                      agent: isSel
                                        ? cur.filter(
                                            (id) => id !== emp.id.toString(),
                                          )
                                        : [...cur, emp.id.toString()],
                                    });
                                  }}
                                  className="flex items-center space-x-3 px-3 py-2 hover:bg-gray-50 rounded-md cursor-pointer"
                                >
                                  <div
                                    className="w-5 h-5 rounded-full flex items-center justify-center border"
                                    style={{
                                      borderColor: currentTheme.color,
                                      backgroundColor: isSel
                                        ? currentTheme.color
                                        : undefined,
                                    }}
                                  >
                                    {isSel ? (
                                      <Check className="w-3 h-3 text-white" />
                                    ) : (
                                      <Plus className="w-3 h-3 text-teal-600" />
                                    )}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-700">
                                      {emp.name}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                      Emp: {emp.employeeId || "N/A"}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Category Filter */}
                {has("LEAD_DETAIL_CATEGORY_FILTER") && (
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <ThemeSelect
                      value={tempFilters.category}
                      onChange={(val) =>
                        setTempFilters({ ...tempFilters, category: val })
                      }
                      options={[
                        { label: "All Categories", value: "" },
                        { label: "Category A", value: "A" },
                        { label: "Category B", value: "B" },
                      ]}
                      className="w-full"
                    />
                  </div>
                )}

                {/* Status Filter */}
                {has("LEAD_STATUS_FILTER") && (
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <ThemeSelect
                      value={tempFilters.status}
                      onChange={(val) =>
                        setTempFilters({ ...tempFilters, status: val })
                      }
                      options={leadStatusOptions}
                      placeholder="Select Status"
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Filter Modal Footer */}
            <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setActiveFilters(resetFilters);
                  setTempFilters(resetFilters);
                  setActiveTab("Fresh");
                  setIsFilterModalOpen(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 mr-4"
              >
                Reset All
              </button>
              <button
                onClick={() => {
                  setActiveFilters(tempFilters);
                  setIsFilterModalOpen(false);
                }}
                className="px-6 py-2 text-sm font-medium text-white rounded-lg"
                style={{ background: currentTheme.color }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Date Range Modal */}
      {showCustomDateModal && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCustomDateModal(false);
          }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        >
          <div
            className={`rounded-2xl shadow-2xl w-full max-w-md p-6 border ${isDark ? "bg-[#111827] border-white/10 text-white" : "bg-white border-gray-100"}`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-800"}`}
              >
                Select Custom Date Range
              </h3>
              <button
                onClick={() => setShowCustomDateModal(false)}
                className={`p-1.5 rounded-lg transition-colors ${isDark ? "text-gray-400 hover:text-white hover:bg-white/10" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <label
                className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
              >
                Start Date – End Date
              </label>
              <RangePicker
                value={
                  tempStartDate && tempEndDate
                    ? [dayjs(tempStartDate), dayjs(tempEndDate)]
                    : null
                }
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setTempStartDate(dates[0].format("YYYY-MM-DD"));
                    setTempEndDate(dates[1].format("YYYY-MM-DD"));
                  } else {
                    setTempStartDate("");
                    setTempEndDate("");
                  }
                }}
                format="DD-MM-YYYY"
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: "10px",
                }}
                className="w-full text-base"
                size="large"
              />
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCustomDateModal(false)}
                className={`px-5 py-2 text-sm font-semibold rounded-xl transition-colors ${isDark ? "bg-white/5 hover:bg-white/10 text-gray-300" : "bg-gray-100 hover:bg-gray-200 text-gray-600"}`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setActiveFilters({
                    ...activeFilters,
                    assignedDate: "custom",
                    customStartDate: tempStartDate,
                    customEndDate: tempEndDate,
                  });
                  setCurrentPage(1);
                  setShowCustomDateModal(false);
                }}
                disabled={!tempStartDate || !tempEndDate}
                className="px-5 py-2 text-sm font-semibold text-white rounded-xl transition-colors hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: currentTheme.color }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
      />

      <LeadHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        lead={selectedHistoryLead}
      />
    </div>
  );
}

export default Leads;
